import type { EduAttendanceStatus } from '../types/schema'

export type { EduAttendanceStatus }

/**
 * Returns the current date (YYYY-MM-DD) in the workspace timezone.
 * Uses Intl.DateTimeFormat with locale 'en-CA' which produces ISO-style
 * YYYY-MM-DD output directly, so no manual part reassembly is needed.
 * Falls back to Asia/Kolkata per EQ52 when timezone is null/undefined.
 */
export function workspaceToday(timezone?: string | null): string {
  const tz = timezone || 'Asia/Kolkata'
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date())
  } catch {
    // Invalid timezone: fall back to device local date in ISO-CA format
    return new Intl.DateTimeFormat('en-CA').format(new Date())
  }
}

export interface EduAttendanceRecordLike {
  status?: EduAttendanceStatus | null
}

/**
 * Computes attendance percentage per EQ57:
 * (PRESENT + LATE) ÷ (marked − ON_LEAVE − EXCUSED)
 * Cancelled and unmarked sessions are excluded / do not count.
 * Returns null when the denominator is 0.
 * Returns a 0–100 percentage number (rounded to at most 1 decimal place).
 */
export function computeAttendancePercent(
  records: Array<EduAttendanceRecordLike | EduAttendanceStatus | null | undefined>,
): number | null {
  if (!Array.isArray(records) || records.length === 0) {
    return null
  }

  let present = 0
  let late = 0
  let onLeave = 0
  let excused = 0
  let marked = 0

  for (const item of records) {
    if (!item) continue
    const status: EduAttendanceStatus | undefined =
      typeof item === 'string' ? item : item.status ?? undefined
    if (!status) continue

    if (status === 'PRESENT') {
      present++
      marked++
    } else if (status === 'LATE') {
      late++
      marked++
    } else if (status === 'ABSENT') {
      marked++
    } else if (status === 'ON_LEAVE') {
      onLeave++
      marked++
    } else if (status === 'EXCUSED') {
      excused++
      marked++
    }
  }

  const denominator = marked - onLeave - excused
  if (denominator <= 0) {
    return null
  }

  const percent = ((present + late) / denominator) * 100
  const rounded1 = Math.round(percent * 10) / 10
  return rounded1 === Math.round(percent) ? Math.round(percent) : rounded1
}

/**
 * Cycles attendance status per EQ54:
 * PRESENT → ABSENT → LATE → PRESENT
 * Default / other statuses (ON_LEAVE, EXCUSED) reset to PRESENT.
 */
export function nextStatus(status: EduAttendanceStatus): EduAttendanceStatus {
  switch (status) {
    case 'PRESENT':
      return 'ABSENT'
    case 'ABSENT':
      return 'LATE'
    case 'LATE':
      return 'PRESENT'
    default:
      return 'PRESENT'
  }
}

/**
 * Shifts an ISO date string (YYYY-MM-DD) by offsetDays days using UTC arithmetic.
 */
export function shiftIsoDays(isoDate: string, offsetDays: number): string {
  const [y, m, d] = isoDate.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + offsetDays))
  const yr = dt.getUTCFullYear()
  const mo = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const da = String(dt.getUTCDate()).padStart(2, '0')
  return `${yr}-${mo}-${da}`
}

export interface CanTakeAttendanceOptions {
  date: string
  todayIso: string
  status?: string | null
  hasSeries: boolean
  isAdmin?: boolean
  isClassTeacher?: boolean
  isSeriesTeacher?: boolean
  isSubstitute?: boolean
}

/**
 * EQ52: Who can take attendance
 * – Cancelled sessions: nobody may mark.
 * – Without a series: nobody may mark.
 * – Future sessions (date > todayIso): nobody may mark.
 * – Admin / Owner / Class teacher: on or before today (date <= todayIso).
 * – Series teacher / Substitute: on today only (date === todayIso).
 * – Other roles: cannot mark.
 */
export function canTakeAttendance({
  date,
  todayIso,
  status,
  hasSeries,
  isAdmin = false,
  isClassTeacher = false,
  isSeriesTeacher = false,
  isSubstitute = false,
}: CanTakeAttendanceOptions): boolean {
  if (status === 'CANCELLED' || !hasSeries) return false
  if (date > todayIso) return false
  if (isAdmin || isClassTeacher) return true
  if (date === todayIso && (isSeriesTeacher || isSubstitute)) return true
  return false
}

export interface IsClassTeacherOptions {
  uid?: string | null
  classElement?: { edu?: { teachers?: Record<string, { role?: string }> } } | null
  elements?: Array<{ id?: string; edu?: { teachers?: Record<string, { role?: string }> } }> | null
  classId?: string | null
}

/**
 * Checks whether the given user has the CLASS_TEACHER role on the class.
 */
export function isClassTeacher({
  uid,
  classElement,
  elements,
  classId,
}: IsClassTeacherOptions): boolean {
  if (!uid) return false
  const target =
    classElement ??
    (classId && elements ? elements.find((el) => el.id === classId) : null)
  return target?.edu?.teachers?.[uid]?.role === 'CLASS_TEACHER'
}

